import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { CarregandoGlobal, TelaCarregando } from './componentes/carregando';
import { Shell } from './layout/shell';
import { useOperador } from './lib/sessao-operador';
import { useSessao } from './lib/sessao';
import { Entrar } from './paginas/entrar';
import { Pessoas } from './paginas/pessoas';
import { PessoaEditar } from './paginas/pessoa-editar';
import { PessoaNova } from './paginas/pessoa-nova';
import { SociedadeNova } from './paginas/sociedade-nova';
import { Sociedades } from './paginas/sociedades';
import { Sociedade } from './paginas/sociedade';
import { Assinatura } from './paginas/assinatura';
import { SociedadeAlterar } from './paginas/sociedade-alterar';
import { AtoNovo } from './paginas/ato-novo';
import { CriarConta } from './paginas/criar-conta';
import { Conta } from './paginas/conta';
import { Usuarios } from './paginas/usuarios';
import { Inicio } from './paginas/inicio';
import { OperadorEntrar } from './paginas/operador-entrar';
import { OperadorEscritorios } from './paginas/operador-escritorios';

/**
 * A área da PLATAFORMA, decidida ANTES de qualquer coisa do produto.
 *
 * Ela sai da árvore normal por completo: não passa pelo `Shell` (que tem o
 * menu do escritório), não passa pela restauração da sessão de usuário, e não
 * cai no login do produto. Sem esta separação, o operador que abrisse
 * `/operador` veria o login do CLIENTE — e a saída natural seria tentar a
 * senha dele ali.
 *
 * As duas sessões coexistem sem se falar: dá para estar logado como contador
 * numa aba e como operador noutra, e é assim que o suporte trabalha.
 */
function AreaDeOperacao(): JSX.Element {
  const token = useOperador((s) => s.token);
  if (!token) return <OperadorEntrar />;

  return (
    <Routes>
      <Route path="/operador" element={<OperadorEscritorios />} />
      <Route path="/operador/*" element={<Navigate to="/operador" replace />} />
    </Routes>
  );
}

export function App(): JSX.Element {
  const { pathname } = useLocation();
  const acesso = useSessao((s) => s.acesso);
  const restaurando = useSessao((s) => s.restaurando);
  const restaurar = useSessao((s) => s.restaurar);

  useEffect(() => {
    void restaurar();
  }, [restaurar]);

  /*
    Antes de tudo: a área de operação não compartilha nada com o produto —
    nem a sessão, nem a casca, nem o login. Ver `AreaDeOperacao`.
  */
  if (pathname.startsWith('/operador')) return <AreaDeOperacao />;

  // Sem este estado, a tela de login pisca antes de a sessão ser restaurada —
  // e o contador que recarregou a página acha que foi deslogado.
  if (restaurando) {
    return (
      <div className="bg-fundo grid min-h-screen place-items-center">
        <TelaCarregando />
      </div>
    );
  }
  /*
    Deslogado, mas nem toda porta é o login: o autocadastro é público, e
    mandá-lo para a tela de entrar seria pedir a senha de uma conta que a
    pessoa veio criar. Qualquer outro caminho continua caindo no login — quem
    abriu um link fundo sem sessão precisa entrar primeiro.
  */
  if (!acesso) {
    return (
      <Routes>
        <Route path="/criar-conta" element={<CriarConta />} />
        <Route path="*" element={<Entrar />} />
      </Routes>
    );
  }

  return (
    <>
      {/*
        Fora das `Routes` de propósito: o indicador vale para a aplicação
        inteira, e montá-lo por rota faria cada tela nova precisar lembrar de
        incluí-lo — que é como um produto acaba com metade das telas sem aviso
        de espera.
      */}
      <CarregandoGlobal />
      <Routes>
        <Route element={<Shell />}>
          {/* A raiz é o PAINEL, não a lista de sociedades. Quem entra vê
              primeiro o que precisa dele; a lista continua a um clique. */}
          <Route path="/" element={<Inicio />} />
          <Route path="/pessoas" element={<Pessoas />} />
          {/* `/pessoas/nova` ANTES de qualquer `/pessoas/:id` que venha depois:
              o React Router casa a rota mais específica, mas manter a ordem
              deixa a intenção explícita para quem acrescentar o detalhe. */}
          <Route path="/pessoas/nova" element={<PessoaNova />} />
          <Route path="/pessoas/:id/editar" element={<PessoaEditar />} />
          <Route path="/sociedades" element={<Sociedades />} />
          <Route path="/sociedades/nova" element={<SociedadeNova />} />
          <Route path="/sociedades/:id" element={<Sociedade />} />
          <Route path="/sociedades/:id/alterar" element={<SociedadeAlterar />} />
          <Route path="/sociedades/:id/ato-novo" element={<AtoNovo />} />
          <Route path="/assinatura" element={<Assinatura />} />
          <Route path="/conta" element={<Conta />} />
          <Route path="/usuarios" element={<Usuarios />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </>
  );
}
